/**
 * Scheda società (V55): stato, Admin Squadra, limiti e account.
 *
 * I limiti si derogano per la singola società e non scendono sotto il
 * consumo, che il server ricontrolla sotto il lock della società. Il nome e
 * il logo restano all'Admin (V45), gli account a V49: qui si leggono.
 *
 * L'Admin Squadra si cambia nominando un account attivo della società, che
 * tiene i suoi ruoli, oppure invitando un indirizzo nuovo. Il vecchio Admin
 * perde il solo ruolo e resta nella società; se non aveva mai accettato
 * l'invito, il suo account si cancella.
 */

import { type FormEvent, useState } from 'react';
import { useParams } from 'react-router-dom';
import { limitiSocietaSchema, nominaAdminSchema } from '@calcio/shared';
import { InfoIcon, OctagonXIcon, PowerIcon, PowerOffIcon } from 'lucide-react';
import { toast } from 'sonner';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Field,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLabel,
  FieldLegend,
  FieldSet,
} from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { chiama } from '../api/client.js';
import { MenuAzioni, PulsanteInvio } from '../comuni/Azioni.js';
import { StatoCaricamento, Testata } from '../comuni/Testata.js';
import { Corpo } from '../comuni/Corpo.js';
import { erroriPerCampo, messaggio, useDati } from '../comuni/dati.js';
import { ETICHETTA_RUOLO, eAdmin, nomeSquadra, statoInvito } from '../amministrazione/ruoli.js';
import { type SchedaSocieta as Scheda, candidatiAdmin, giorno, rapporto } from './piattaforma.js';
import { useConferma } from '../comuni/Conferma.js';

export function SchedaSocieta() {
  const { id = '' } = useParams();
  const { dati, errore, ricarica } = useDati<Scheda>(`/piattaforma/societa/${id}`);
  const [inCorso, setInCorso] = useState(false);
  const [nomina, setNomina] = useState(false);
  const [chiedi, conferma] = useConferma();

  if (!dati)
    return (
      <Corpo>
        <StatoCaricamento errore={errore} />
      </Corpo>
    );

  const agisci = async (lavoro: () => Promise<unknown>, esito?: string) => {
    setInCorso(true);
    try {
      await lavoro();
      await ricarica();
      if (esito) toast.success(esito);
    } catch (e) {
      toast.error(messaggio(e));
    } finally {
      setInCorso(false);
    }
  };

  const impostaStato = async () => {
    const si = await chiedi(
      dati.attiva
        ? {
            titolo: `Disattivare ${dati.nome}?`,
            testo: 'Chi è dentro esce subito e nessuno può più accedere finché non la riattivi.',
            azione: 'Disattiva',
            distruttiva: true,
          }
        : {
            titolo: `Riattivare ${dati.nome}?`,
            testo: 'Account e inviti tornano validi.',
            azione: 'Riattiva',
          },
    );
    if (!si) return;
    void agisci(
      () =>
        chiama(`/piattaforma/societa/${id}/stato`, {
          metodo: 'PUT',
          corpo: { attiva: !dati.attiva },
        }),
      dati.attiva ? `${dati.nome} disattivata.` : `${dati.nome} riattivata.`,
    );
  };

  const reinvia = () =>
    void agisci(
      () => chiama(`/piattaforma/societa/${id}/admin/reinvia`, { metodo: 'POST' }),
      `Invito inviato di nuovo a ${dati.admin?.email}.`,
    );

  return (
    <div>
      <Testata
        titolo={dati.nome}
        indietro="/"
        azione={
          <MenuAzioni
            voci={[
              {
                etichetta: dati.attiva ? 'Disattiva' : 'Riattiva',
                Icona: dati.attiva ? PowerOffIcon : PowerIcon,
                disabled: inCorso,
                onSelect: () => void impostaStato(),
                distruttiva: dati.attiva,
              },
            ]}
          />
        }
      >
        <p className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
          {dati.attiva ? (
            <Badge variant="outline">Attiva</Badge>
          ) : (
            <Badge variant="secondary">Disattivata</Badge>
          )}
          <span>
            {dati.slug} · creata il {giorno(dati.creataIl)}
          </span>
        </p>
      </Testata>

      <Corpo className="flex flex-col gap-8">
        {!dati.attiva && (
          <Alert>
            <InfoIcon />
            <AlertTitle>Società disattivata</AlertTitle>
            <AlertDescription>
              Nessuno dei suoi account può accedere e gli inviti in attesa non si accettano.
            </AlertDescription>
          </Alert>
        )}

        <FieldSet>
          <FieldLegend>Admin Squadra</FieldLegend>
          <div className="flex flex-wrap items-center justify-between gap-3">
            {dati.admin ? (
              <span className="flex flex-col">
                <span className="flex items-center gap-2 font-medium">
                  {dati.admin.nome} {dati.admin.cognome}
                  {dati.admin.invito && (
                    <Badge variant={dati.admin.invito.scaduto ? 'destructive' : 'outline'}>
                      {dati.admin.invito.scaduto ? 'Invito scaduto' : 'Invitato'}
                    </Badge>
                  )}
                </span>
                <span className="text-sm text-muted-foreground">{dati.admin.email}</span>
                {dati.admin.invito && (
                  <span className="text-xs text-muted-foreground">
                    {statoInvito(dati.admin.invito)}
                  </span>
                )}
              </span>
            ) : (
              <span className="text-muted-foreground">
                Nessun Admin Squadra: nessuno può amministrare la società.
              </span>
            )}
            <div className="flex gap-2">
              {dati.admin?.invito && (
                <Button
                  type="button"
                  variant="outline"
                  disabled={inCorso || !dati.attiva}
                  onClick={reinvia}
                >
                  Reinvia invito
                </Button>
              )}
              <Button
                type="button"
                variant="outline"
                onClick={() => setNomina(true)}
                disabled={inCorso}
              >
                {dati.admin ? 'Cambia Admin Squadra' : 'Nomina Admin Squadra'}
              </Button>
            </div>
          </div>
        </FieldSet>

        <FormLimiti
          key={JSON.stringify(dati.limiti)}
          scheda={dati}
          onSalvati={async () => {
            await ricarica();
            toast.success('Limiti salvati.');
          }}
        />

        <FieldSet>
          <FieldLegend>Account</FieldLegend>
          <FieldDescription>
            In sola lettura: li gestisce l'Admin Squadra da Account e ruoli.
          </FieldDescription>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Account</TableHead>
                <TableHead className="whitespace-normal">Ruoli nella stagione corrente</TableHead>
                <TableHead>Ultimo accesso</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {dati.account.map((a) => {
                const correnti = a.ruoli.filter(
                  (r) => r.ruolo !== 'admin_squadra' && r.stagioneCorrente,
                );
                return (
                  <TableRow key={a.id}>
                    <TableCell>
                      <span className="flex flex-col">
                        <span className="flex items-center gap-2 font-medium">
                          {a.cognome} {a.nome}
                          {!a.attivo && <Badge variant="secondary">Disattivato</Badge>}
                          {a.invito && (
                            <Badge variant={a.invito.scaduto ? 'destructive' : 'outline'}>
                              {a.invito.scaduto ? 'Invito scaduto' : 'Invitato'}
                            </Badge>
                          )}
                        </span>
                        <span className="text-xs text-muted-foreground">{a.email}</span>
                      </span>
                    </TableCell>
                    <TableCell className="whitespace-normal">
                      <span className="flex flex-wrap items-center gap-1">
                        {eAdmin(a) && <Badge>Admin Squadra</Badge>}
                        {correnti.map((r) => (
                          <Badge key={r.id} variant="outline">
                            {ETICHETTA_RUOLO[r.ruolo]} · {nomeSquadra(r)}
                          </Badge>
                        ))}
                        {!eAdmin(a) && correnti.length === 0 && (
                          <span className="text-sm text-muted-foreground">Nessun ruolo</span>
                        )}
                      </span>
                    </TableCell>
                    <TableCell className="tabular-nums">
                      {a.ultimoAccesso ? giorno(a.ultimoAccesso) : '—'}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </FieldSet>

        <Dialog open={nomina} onOpenChange={setNomina}>
          <DialogContent>
            {nomina && (
              <FormNomina
                scheda={dati}
                onChiudi={() => setNomina(false)}
                onNominato={async (esito) => {
                  setNomina(false);
                  await ricarica();
                  toast.success(esito);
                }}
              />
            )}
          </DialogContent>
        </Dialog>
        {conferma}
      </Corpo>
    </div>
  );
}

const ETICHETTE_LIMITI = {
  maxSquadreStagione: { etichetta: 'Squadre per stagione', consumo: 'squadreStagione' },
  maxGiocatori: { etichetta: 'Anagrafiche', consumo: 'giocatori' },
  maxAccount: { etichetta: 'Account', consumo: 'account' },
  maxMessaggiAssistente: { etichetta: "Domande all'assistente al mese", consumo: 'assistente' },
} as const;

function FormLimiti({ scheda, onSalvati }: { scheda: Scheda; onSalvati: () => Promise<void> }) {
  const [campi, setCampi] = useState({
    maxSquadreStagione: String(scheda.limiti.squadreStagione.massimo),
    maxGiocatori: String(scheda.limiti.giocatori.massimo),
    maxAccount: String(scheda.limiti.account.massimo),
    maxMessaggiAssistente: String(scheda.limiti.assistente.massimo),
  });
  const [errori, setErrori] = useState<Record<string, string>>({});
  const [erroreServer, setErroreServer] = useState<string | null>(null);
  const [inCorso, setInCorso] = useState(false);

  const invia = async (e: FormEvent) => {
    e.preventDefault();
    const esito = limitiSocietaSchema.safeParse(campi);
    if (!esito.success) return setErrori(erroriPerCampo(esito.error));
    setErrori({});
    setErroreServer(null);
    setInCorso(true);
    try {
      await chiama(`/piattaforma/societa/${scheda.id}/limiti`, {
        metodo: 'PUT',
        corpo: esito.data,
      });
      await onSalvati();
    } catch (errore) {
      setErroreServer(messaggio(errore));
      setInCorso(false);
    }
  };

  return (
    <form onSubmit={(e) => void invia(e)}>
      <FieldSet>
        <FieldLegend>Limiti</FieldLegend>
        <FieldDescription>
          Deroga per questa società. Un limite non scende sotto il consumo attuale, tranne le
          domande all'assistente, che ripartono il primo del mese.
        </FieldDescription>
        <FieldGroup className="grid grid-cols-1 gap-4 @lg:grid-cols-2">
          {(Object.keys(ETICHETTE_LIMITI) as (keyof typeof ETICHETTE_LIMITI)[]).map((k) => {
            const { etichetta, consumo } = ETICHETTE_LIMITI[k];
            return (
              <Field key={k} data-invalid={Boolean(errori[k]) || undefined}>
                <FieldLabel htmlFor={`limite-${k}`}>{etichetta}</FieldLabel>
                <Input
                  id={`limite-${k}`}
                  type="number"
                  inputMode="numeric"
                  min={1}
                  value={campi[k]}
                  aria-invalid={Boolean(errori[k]) || undefined}
                  onChange={(e) => setCampi({ ...campi, [k]: e.target.value })}
                />
                <FieldDescription className="tabular-nums">
                  In uso {rapporto(scheda.limiti[consumo])}
                  {k === 'maxSquadreStagione' && ', stagione corrente'}
                  {k === 'maxMessaggiAssistente' && ', questo mese'}
                </FieldDescription>
                {errori[k] && <FieldError>{errori[k]}</FieldError>}
              </Field>
            );
          })}
        </FieldGroup>
        {erroreServer && (
          <Alert variant="destructive">
            <OctagonXIcon />
            <AlertTitle>Salvataggio non riuscito</AlertTitle>
            <AlertDescription>{erroreServer}</AlertDescription>
          </Alert>
        )}
        <div className="flex justify-end">
          <PulsanteInvio type="submit" inCorso={inCorso}>
            Salva limiti
          </PulsanteInvio>
        </div>
      </FieldSet>
    </form>
  );
}

function FormNomina({
  scheda,
  onChiudi,
  onNominato,
}: {
  scheda: Scheda;
  onChiudi: () => void;
  onNominato: (esito: string) => Promise<void>;
}) {
  const candidati = candidatiAdmin(scheda.account, scheda.admin?.id ?? null);
  const [modo, setModo] = useState<'account' | 'invito'>(
    candidati.length > 0 ? 'account' : 'invito',
  );
  const [accountId, setAccountId] = useState('');
  const [campi, setCampi] = useState({ email: '', nome: '', cognome: '' });
  const [errori, setErrori] = useState<Record<string, string>>({});
  const [erroreServer, setErroreServer] = useState<string | null>(null);
  const [inCorso, setInCorso] = useState(false);

  const invia = async (e: FormEvent) => {
    e.preventDefault();
    const esito = nominaAdminSchema.safeParse(
      modo === 'account' ? { modo, accountId } : { modo, ...campi },
    );
    if (!esito.success) {
      const perCampo = erroriPerCampo(esito.error);
      if (modo === 'account') perCampo.accountId = 'Scegli un account.';
      return setErrori(perCampo);
    }
    setErrori({});
    setErroreServer(null);
    setInCorso(true);
    try {
      await chiama(`/piattaforma/societa/${scheda.id}/admin`, {
        metodo: 'POST',
        corpo: esito.data,
      });
      const scelto = candidati.find((a) => a.id === accountId);
      await onNominato(
        esito.data.modo === 'invito'
          ? `Invito da Admin Squadra partito verso ${esito.data.email}.`
          : `${scelto?.nome ?? ''} ${scelto?.cognome ?? ''} è il nuovo Admin Squadra.`,
      );
    } catch (errore) {
      setErroreServer(messaggio(errore));
      setInCorso(false);
    }
  };

  const campo = (chiave: keyof typeof campi, etichetta: string, tipo = 'text') => (
    <Field data-invalid={Boolean(errori[chiave]) || undefined}>
      <FieldLabel htmlFor={`admin-${chiave}`}>{etichetta}</FieldLabel>
      <Input
        id={`admin-${chiave}`}
        type={tipo}
        value={campi[chiave]}
        aria-invalid={Boolean(errori[chiave]) || undefined}
        onChange={(e) => setCampi({ ...campi, [chiave]: e.target.value })}
      />
      {errori[chiave] && <FieldError>{errori[chiave]}</FieldError>}
    </Field>
  );

  const vecchio = scheda.admin;

  return (
    <form onSubmit={(e) => void invia(e)} className="flex flex-col gap-6">
      <DialogHeader>
        <DialogTitle>{vecchio ? 'Cambia Admin Squadra' : 'Nomina Admin Squadra'}</DialogTitle>
        <DialogDescription>
          {!vecchio
            ? `La società ${scheda.nome} non ha un Admin Squadra.`
            : vecchio.invito
              ? `L'invito di ${vecchio.nome} ${vecchio.cognome} non è mai stato accettato: il suo account viene cancellato.`
              : `${vecchio.nome} ${vecchio.cognome} perde il ruolo ma resta nella società, con gli altri ruoli che ha.`}
        </DialogDescription>
      </DialogHeader>

      <FieldGroup>
        <Field>
          <FieldLabel>Chi</FieldLabel>
          <ToggleGroup
            type="single"
            variant="outline"
            value={modo}
            onValueChange={(v) => v && setModo(v as 'account' | 'invito')}
          >
            <ToggleGroupItem value="account" disabled={candidati.length === 0}>
              Account della società
            </ToggleGroupItem>
            <ToggleGroupItem value="invito">Nuovo indirizzo</ToggleGroupItem>
          </ToggleGroup>
          {candidati.length === 0 && (
            <FieldDescription>
              Nessun altro account attivo nella società: si può solo invitare.
            </FieldDescription>
          )}
        </Field>

        {modo === 'account' ? (
          <Field data-invalid={Boolean(errori.accountId) || undefined}>
            <FieldLabel htmlFor="admin-account">Account</FieldLabel>
            <Select value={accountId} onValueChange={setAccountId}>
              <SelectTrigger id="admin-account" className="w-full">
                <SelectValue placeholder="Scegli un account" />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  {candidati.map((a) => (
                    <SelectItem key={a.id} value={a.id}>
                      {a.cognome} {a.nome} · {a.email}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
            <FieldDescription>Tiene i ruoli di squadra che ha già.</FieldDescription>
            {errori.accountId && <FieldError>{errori.accountId}</FieldError>}
          </Field>
        ) : (
          <>
            {campo('email', 'Email', 'email')}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {campo('nome', 'Nome')}
              {campo('cognome', 'Cognome')}
            </div>
            <FieldDescription>
              L'invito vale 7 giorni e occupa un posto del limite di account.
            </FieldDescription>
          </>
        )}
      </FieldGroup>

      {erroreServer && (
        <Alert variant="destructive">
          <OctagonXIcon />
          <AlertTitle>Salvataggio non riuscito</AlertTitle>
          <AlertDescription>{erroreServer}</AlertDescription>
        </Alert>
      )}

      <DialogFooter>
        <Button type="button" variant="outline" onClick={onChiudi}>
          Annulla
        </Button>
        <PulsanteInvio type="submit" inCorso={inCorso}>
          {modo === 'account' ? 'Nomina' : 'Invita'}
        </PulsanteInvio>
      </DialogFooter>
    </form>
  );
}
